import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
} from "@mui/material";
import { IGameResult, IPenaltyItem } from "../ts/Definitions";
import { SelectedSettingsContext } from "../ts/Contexts";
import { IFruitMachineGame } from "../ts/Definitions";

interface FruitMachineDialogProps {
  game: IFruitMachineGame;
  onClose: (result: IGameResult) => void;
  penaltyKeys?: string[];
}

const SYMBOLS = ["🍒", "🍋", "🔔", "⭐", "7️⃣", "🍊", "🍇"];

const SlotPaper: React.FC<{ value: string; large?: boolean }> = ({ value, large }) => (
  <Paper
    elevation={2}
    sx={{
      width: large ? 110 : 80,
      height: large ? 110 : 80,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 2,
      fontSize: large ? 40 : 28,
      mr: 1,
      ml: 1,
    }}
  >
    <Typography variant="h4" component="span">
      {value}
    </Typography>
  </Paper>
);

const FruitMachineDialog: React.FC<FruitMachineDialogProps> = ({ game, onClose, penaltyKeys }) => {
  const settings = useContext(SelectedSettingsContext);
  const { initialScore, requiredSpins, key: gameKey, userid, username, name } = game;
  const [penalties, setPenalties] = useState<IPenaltyItem[]>([]);
  useEffect(() => {
    const newPens = penaltyKeys?.map(k => settings.getPenalty?.(k)).filter(Boolean) as IPenaltyItem[] || [];
    setPenalties(newPens);
  }, [penaltyKeys]);

  const [currentCount, setCurrentCount] = useState<number>(initialScore ?? 0);
  const [spinsDone, setSpinsDone] = useState<number>(0);
  const [finished, setFinished] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [spinning, setSpinning] = useState<boolean>(false);
  const [slots, setSlots] = useState<string[]>([
    SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)],
    SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)],
    SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)],
  ]);

  // Refs to manage intervals/timeouts for a spin
  const intervalsRef = useRef<number[]>([]);
  const timeoutsRef = useRef<number[]>([]);

  useEffect(() => {
    return () => {
      // cleanup any timers on unmount
      intervalsRef.current.forEach(id => clearInterval(id));
      timeoutsRef.current.forEach(id => clearTimeout(id));
    };
  }, []);

  const handleSpin = () => {
    if (spinning || finished) return;
    setSpinning(true);
    setMessage(null);

    // Start quick randomization for each slot
    const localIntervals: number[] = [];
    const localTimeouts: number[] = [];

    const tempSlots = [...slots];

    for (let i = 0; i < 3; i++) {
      // rapidly change symbol for this slot
      const intId = window.setInterval(() => {
        tempSlots[i] = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
        setSlots([...tempSlots]);
      }, 60 + Math.floor(Math.random() * 40)); // small jitter
      localIntervals.push(intId);
      // after a staggered delay, settle this slot to final value
      const settleDelay = 600 + i * 220; // staggered stops
      const toId = window.setTimeout(() => {
        clearInterval(intId);
        const final = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
        tempSlots[i] = final;
        setSlots([...tempSlots]);
      }, settleDelay);
      localTimeouts.push(toId);
    }

    intervalsRef.current.push(...localIntervals);
    timeoutsRef.current.push(...localTimeouts);

    // after all settled compute outcome
    const totalSettleDelay = 600 + 2 * 220 + 80;
    const finishTimeout = window.setTimeout(() => {
      // clear any leftover intervals
      localIntervals.forEach(id => clearInterval(id));

      // evaluate matches
      const final = [...tempSlots];
      const counts: Record<string, number> = {};
      final.forEach(s => (counts[s] = (counts[s] || 0) + 1));
      const threeMatch = Object.values(counts).some(c => c === 3);
      const twoMatch = Object.values(counts).some(c => c === 2);

      let newCount = currentCount;
      if (threeMatch) {
        newCount = newCount * 0.5; // reduce by 50%
        setMessage("Three of a kind! -50%");
      } else if (twoMatch) {
        newCount = newCount * 0.9; // reduce by 10%
        setMessage("Two match! -10%");
      } else {
        newCount = newCount * 1.05; // increase by 5%
        setMessage("+5% boost");
      }

      // Round to sensible precision (keep cents)
      newCount = Math.round(newCount * 100) / 100;

      setCurrentCount(newCount);
      setSpinsDone(s => s + 1);
      setSpinning(false);

      // cleanup local timers from refs
      localTimeouts.forEach(id => {
        const idx = timeoutsRef.current.indexOf(id);
        if (idx >= 0) timeoutsRef.current.splice(idx, 1);
        clearTimeout(id);
      });
      localIntervals.forEach(id => {
        const idx = intervalsRef.current.indexOf(id);
        if (idx >= 0) intervalsRef.current.splice(idx, 1);
        clearInterval(id);
      });

      // If finished number of spins, mark finished and show result
      if (spinsDone + 1 >= requiredSpins) {
        setFinished(true);
      }
    }, totalSettleDelay);

    timeoutsRef.current.push(finishTimeout);
  };

  const handleClose = () => {
    // return penaltyCount equal to remaining count (rounded)
    const penaltyCount = Math.round(currentCount);
    onClose({
      gameKey,
      penaltyCount,
      completed: spinsDone,
      total: requiredSpins,
      totalTime: spinsDone,
      mistakes: 0,
      userid: userid ?? settings.getControl().userid,
      username: username ?? settings.getControl().username,
      date: new Date(),
    } as IGameResult);
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <DialogTitle>{name ? `Fruit Machine: ${name}` : "Fruit Machine"}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Spins required: {requiredSpins} — Spins done: {spinsDone}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Current count: <b>{currentCount.toFixed(0)}</b>
        </Typography>
        {penalties.length > 0 && (
          <Alert severity="warning" sx={{ my: 1 }}>
            Penalty: {penalties.map(p => p.name).join(", ")}
          </Alert>
        )}
        <Box sx={{ mt: 2, mb: 2, display: "flex", justifyContent: "center" }}>
          <Grid container spacing={0} justifyContent="center" alignItems="center" sx={{ maxWidth: 380 }}>
            <Grid item>
              <SlotPaper value={slots[0]} large />
            </Grid>
            <Grid item>
              <SlotPaper value={slots[1]} large />
            </Grid>
            <Grid item>
              <SlotPaper value={slots[2]} large />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Alert severity={finished ? "info" : "success"}>{message ?? (spinning ? "<spinning...>" : "Click to Spin")}</Alert>
        </Box>

        {!finished ? (
          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Press Spin to play. After {requiredSpins} spins, game ends and remaining count will be returned as penalty.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              Game complete. Final count: <b>{currentCount}</b>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!finished && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSpin}
            disabled={spinning}
          >
            {spinning ? "Spinning..." : "Spin"}
          </Button>
        )}

        <Button
          variant="outlined"
          disabled={!finished}
          color={finished ? "primary" : "secondary"}
          onClick={handleClose}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FruitMachineDialog;